// /dtv/$category/for/$nationality — Triple-key TH page (DTV × nationality)
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { DTV_CATEGORIES } from "@/data/dtv-categories";
import { VISA_NATIONALITIES } from "@/data/visa-nationalities";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, howToSchema, faqPageSchema } from "@/lib/aeo";
import { aggregateRatingNode, reviewNodes } from "@/lib/programmatic-ratings";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { isIndexable } from "@/data/indexable-whitelist";
import { ContactCTA, FaqAccordion, DirectAnswer, KeyFactsTable } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import { Phone, MessageCircle, ArrowRight, Sparkles, FileText, Wallet, Clock, ShieldCheck, BadgeCheck, Globe2 } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/dtv/$category/for/$nationality")({
  loader: async ({ params }) => {
    const [{ DTV_CATEGORIES: LIST }, { VISA_NATIONALITIES: NATS }] = await Promise.all([
      import("@/data/dtv-categories"),
      import("@/data/visa-nationalities"),
    ]);
    const cat = LIST.find((x) => x.slug === params.category);
    const nat = NATS.find((n) => n.slug === params.nationality);
    if (!cat || !nat) throw notFound();
    return { cat, nat };
  },
  head: ({ loaderData }) => {
    const cat = loaderData?.cat;
    const nat = loaderData?.nat;
    if (!cat || !nat) return { meta: [{ title: ctaTitle("ไม่พบหน้า", "/dtv/$category/for/$nationality", "th")}] };
    const url = `${SITE}/dtv/${cat.slug}/for/${nat.slug}`;
    const title = `${cat.titleTh} สำหรับชาว${nat.nameTh} — ขั้นตอน เอกสาร ค่าธรรมเนียม 2026 | NYC`;
    const desc = `ยื่นขอ ${cat.titleTh} สำหรับพลเมือง${nat.countryEn} — ${nat.visaExempt ? `เข้าไทย Visa Exempt ${nat.visaExempt} วัน` : "ต้องยื่นวีซ่าก่อนเข้าไทย"} ค่าธรรมเนียมรัฐ 10,000 บาท + บริการ NYC ${cat.servicePriceTHB.toLocaleString()} บาท เสร็จใน ${cat.processingDays}.`;
    const faqs = [
      { q: `พลเมือง${nat.countryEn}ขอ ${cat.titleTh} ได้ไหม?`, a: `ได้ — ${cat.titleTh} เปิดรับทุกสัญชาติรวมถึง${nat.countryEn} ต้องยื่นที่สถานทูตไทยใน${nat.countryEn} หรือทางออนไลน์ thaievisa.go.th พร้อมหลักฐานการเงิน 500,000 บาท` },
      { q: `ชาว${nat.countryEn} ต้องเข้าไทยก่อนยื่นไหม?`, a: nat.visaExempt > 0 ? `ไม่จำเป็น — เข้าไทย Visa Exempt ${nat.visaExempt} วัน แล้วยื่น DTV ต่อในไทยได้ที่กรมการกงสุล หรือยื่น e-Visa ก่อนเดินทาง` : `แนะนำให้ยื่น e-Visa ก่อนเดินทาง เพราะไม่ได้รับ Visa Exempt` },
      { q: `เอกสารต้องแปลภาษาไหน?`, a: `เอกสารทุกฉบับต้องเป็นภาษาอังกฤษ หรือแปลภาษาไทยรับรองโดยผู้แปลที่ขึ้นทะเบียนกับศาลยุติธรรม/MFA — NYC แปล+รับรองให้ครบในที่เดียว` },
      { q: `${nat.countryEn}เสียภาษีในไทยไหมถ้าอยู่ DTV?`, a: `อยู่เกิน 180 วัน/ปี = Tax Resident ต้องยื่น PND.90/91 เฉพาะรายได้ที่นำเข้าไทย ปรึกษานักบัญชีที่ลงทะเบียน` },
      { q: `ราคารวมของ NYC เท่าไหร่?`, a: `บริการเตรียมเอกสาร+แปล+ยื่น e-Visa ${cat.servicePriceTHB.toLocaleString()} บาท บวกค่าธรรมเนียมรัฐ 10,000 บาท รวม ${(cat.servicePriceTHB + 10000).toLocaleString()} บาท` },
      { q: `ใช้เวลาดำเนินการกี่วัน?`, a: `${cat.processingDays} หลังยื่นครบเอกสาร — express +3,500 บาท ลดเหลือ 3 วันทำการ` },
    ];
    return {
      meta: [
        ...(isIndexable(url) ? [] : [{ name: "robots", content: "noindex,follow" }]),
        { title: ctaTitle(title, "/dtv/$category/for/$nationality", "th") }, { name: "description", content: ctaDesc(desc, "/dtv/$category/for/$nationality", "th")},
        { name: "keywords", content: `DTV ${nat.demonymEn}, ${cat.titleTh} ${nat.nameTh}, Destination Thailand Visa ${nat.countryEn}, ${nat.keywords.join(", ")}` },
        { property: "og:title", content: ctaTitle(title, "/dtv/$category/for/$nationality", "th")}, { property: "og:description", content: ctaDesc(desc, "/dtv/$category/for/$nationality", "th")},
        { property: "og:url", content: url }, { property: "og:locale", content: "th_TH" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/dtv/${cat.slug}/for/${nat.slug}`, `/en/dtv/${cat.slug}/for/${nat.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: `${cat.titleTh} สำหรับชาว${nat.nameTh}`,
              serviceType: `${cat.titleEn} for ${nat.demonymEn} citizens`, description: desc,
              inLanguage: "th",
              provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Legal & Notary Service", telephone: "+66832494999", url: SITE },
              areaServed: [{ "@type": "Country", name: "Thailand" }, { "@type": "Country", name: nat.countryEn }],
              offers: { "@type": "Offer", price: String(cat.servicePriceTHB), priceCurrency: "THB", availability: "https://schema.org/InStock" },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
          howToSchema({
            name: `วิธีขอ ${cat.titleTh} สำหรับชาว${nat.nameTh}`,
            description: `${cat.taglineTh} — guide สำหรับพลเมือง${nat.countryEn}`,
            totalTime: "P15D",
            estimatedCost: { currency: "THB", value: String(cat.servicePriceTHB + 10000) },
            inLanguage: "th",
            steps: cat.stepsTh,
          }),
          faqPageSchema(faqs),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "BreadcrumbList", "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "หน้าแรก", item: SITE },
                { "@type": "ListItem", position: 2, name: "DTV", item: `${SITE}/dtv` },
                { "@type": "ListItem", position: 3, name: cat.titleTh, item: `${SITE}/dtv/${cat.slug}` },
                { "@type": "ListItem", position: 4, name: nat.countryEn, item: url },
              ],
            }),
          },
          ...reviewNodes(url, `${url}#service`, 2).map((r) => ({ type: "application/ld+json" as const, children: JSON.stringify(r) })),
        ]),
      ],
    };
  },
  component: DtvCategoryNationalityPage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">ไม่พบหน้า</h1>
      <Link to="/dtv" className="mt-4 inline-block text-amber-300 underline">กลับหน้า DTV</Link>
    </main>
  ),
});

function DtvCategoryNationalityPage() {
  const { cat, nat } = Route.useLoaderData();
  const otherNats = VISA_NATIONALITIES.filter((n) => n.slug !== nat.slug).slice(0, 12);
  const otherCats = DTV_CATEGORIES.filter((c) => c.slug !== cat.slug).slice(0, 6);
  const faqs = [
    { q: `พลเมือง${nat.countryEn}ขอ ${cat.titleTh} ได้ไหม?`, a: `ได้ — ${cat.titleTh} เปิดรับทุกสัญชาติรวมถึง${nat.countryEn} ยื่น e-Visa ที่ thaievisa.go.th หรือสถานทูตไทยใน${nat.countryEn} พร้อมหลักฐานการเงิน 500,000 บาท` },
    { q: `ชาว${nat.countryEn} ต้องเข้าไทยก่อนยื่นไหม?`, a: nat.visaExempt > 0 ? `ไม่จำเป็น — เข้า Visa Exempt ${nat.visaExempt} วันแล้วยื่นในไทยได้` : `แนะนำยื่น e-Visa ก่อนเดินทาง` },
    { q: `เอกสารต้องแปลภาษาไหน?`, a: `ภาษาอังกฤษ หรือแปลไทยรับรอง MFA — NYC ดำเนินการครบในที่เดียว` },
    { q: `เสียภาษีในไทยไหม?`, a: `อยู่เกิน 180 วัน/ปี = Tax Resident ต้องยื่น PND.90/91 เฉพาะรายได้ที่นำเข้าไทย` },
    { q: `ราคารวมเท่าไหร่?`, a: `${cat.servicePriceTHB.toLocaleString()} + 10,000 บาท (รัฐ) = ${(cat.servicePriceTHB + 10000).toLocaleString()} บาท` },
    { q: `ใช้เวลากี่วัน?`, a: `${cat.processingDays} — Express +3,500 บาท เหลือ 3 วันทำการ` },
  ];

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_55%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/dtv" className="hover:text-gold">DTV</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/dtv/$category" params={{ category: cat.slug }} className="hover:text-gold">{cat.titleTh}</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{nat.flag} {nat.countryEn}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <Sparkles className="h-3.5 w-3.5" /> {cat.icon} DTV · {nat.demonymEn}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">
            {cat.titleTh}<br />สำหรับพลเมือง<span className="text-gold">{nat.countryEn}</span>
          </h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">
            {cat.taglineTh} — guide เฉพาะของชาว{nat.nameTh}: {nat.visaExempt ? `เข้า Visa Exempt ${nat.visaExempt} วันก่อนยื่นได้` : "ต้องยื่นก่อนเดินทาง"} ค่าธรรมเนียมรัฐ 10,000 + บริการ NYC {cat.servicePriceTHB.toLocaleString()} บาท เสร็จใน {cat.processingDays}.
          </p>
          <dl className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { k: "ค่าธรรมเนียม", v: `฿${(cat.servicePriceTHB + 10000).toLocaleString()}` },
              { k: "ระยะเวลา", v: cat.processingDays },
              { k: "Visa Exempt", v: nat.visaExempt ? `${nat.visaExempt} วัน` : "ไม่มี" },
              { k: "Rating", v: "4.9 / 1,247" },
            ].map((s) => (
              <div key={s.k} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur">
                <dt className="text-[10px] font-bold uppercase tracking-widest text-gold">{s.k}</dt>
                <dd className="mt-1 text-sm font-extrabold text-white sm:text-base">{s.v}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md">
              <MessageCircle className="h-4 w-4" /> แชต LINE
            </a>
            <a href="tel:+66832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md">
              <Phone className="h-4 w-4" /> +66 83 249 4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable · AI Overview ready
          </span>
          NYC ช่วยพลเมือง{nat.countryEn}ยื่น {cat.titleTh} — เตรียมเอกสาร แปลรับรอง MFA และยื่น e-Visa ที่ thaievisa.go.th — เสร็จใน {cat.processingDays} ราคาเริ่มต้น {cat.servicePriceTHB.toLocaleString()} บาท (ไม่รวมค่าธรรมเนียมรัฐ 10,000 บาท) เหมาะกับ{cat.whoTh}
        </blockquote>

        <div className="mt-8 grid gap-6 lg:grid-cols-[1.2fr_1fr]">
          <DirectAnswer entity={`DTV สำหรับ${nat.countryEn}`}>
            {cat.quickAnswerTh} สำหรับพลเมือง{nat.countryEn} {nat.notes}
          </DirectAnswer>
          <KeyFactsTable rows={[
            { k: "สัญชาติ", v: `${nat.flag} ${nat.countryEn}` },
            { k: "หมวด DTV", v: cat.titleEn },
            { k: "Visa Exempt", v: nat.visaExempt ? `${nat.visaExempt} วัน` : "ไม่ได้รับ" },
            { k: "ค่าธรรมเนียมรัฐ", v: "฿10,000" },
            { k: "บริการ NYC", v: `฿${cat.servicePriceTHB.toLocaleString()}` },
            { k: "ระยะเวลา", v: cat.processingDays },
          ]} />
        </div>

        <section className="mt-12">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">ทำไมพลเมือง{nat.countryEn}เลือก {cat.titleTh}</h2>
          <p className="mt-4 text-[15px] leading-relaxed text-slate-700">
            {nat.notes} ทำให้ {cat.titleTh} เป็นทางเลือกที่ลงตัวที่สุดสำหรับ{nat.demonymEn}ที่ต้องการอยู่ไทยระยะยาวโดยไม่ต้องเปิดบริษัทหรือซื้ออสังหาฯ. DTV ให้สิทธิเข้า-ออกไม่จำกัด 5 ปี พักได้ 180 วัน/ครั้ง ต่อได้อีก 180 วัน รวม 360 วัน/ปี — เหมาะกับ{cat.whoTh}.
          </p>
          <p className="mt-4 text-[15px] leading-relaxed text-slate-700">
            จุดต่างของ NYC: เราเป็น Notarial Services Attorney ขึ้นทะเบียนสภาทนายความ + แปลรับรอง MFA ในที่เดียว — พลเมือง{nat.countryEn}ส่งเอกสารผ่าน LINE/email ได้ ไม่ต้องเดินทางมาเอง ทีมจัดการตั้งแต่แปล รับรอง ยื่น e-Visa จนได้รับ approval ใน {cat.processingDays}.
          </p>
        </section>

        <section className="mt-12 grid gap-5 sm:grid-cols-2">
          {[
            { icon: FileText, title: "เอกสารหลัก", items: cat.docsTh as string[] },
            { icon: ShieldCheck, title: "Sponsor / proof", items: [cat.sponsorTh, cat.whoTh] as string[] },
            { icon: Clock, title: "Timeline", items: cat.stepsTh.map((s: { name: string; text: string }) => `${s.name} — ${s.text.slice(0, 60)}…`) },
            { icon: Wallet, title: "ค่าใช้จ่ายรวม", items: [`บริการ NYC ฿${cat.servicePriceTHB.toLocaleString()}`, "ค่าธรรมเนียมรัฐ ฿10,000", "Notary +฿1,500/เอกสาร (ถ้าจำเป็น)", "Express 3 วัน +฿3,500"] as string[] },
          ].map((b) => (
            <div key={b.title} className="rounded-2xl border border-slate-200 bg-slate-50/60 p-5">
              <div className="flex items-center gap-2"><b.icon className="h-5 w-5 text-amber-600" /><h3 className="font-display text-lg font-extrabold text-navy-deep">{b.title}</h3></div>
              <ul className="mt-3 space-y-1.5 text-sm text-slate-700">
                {b.items.slice(0, 6).map((it: string, i: number) => <li key={i} className="flex gap-2"><span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500" />{it}</li>)}
              </ul>
            </div>
          ))}
        </section>

        <section className="mt-12 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 sm:p-8">
          <div className="text-[11px] font-bold uppercase tracking-widest text-amber-700">แพ็กเกจสำหรับ{nat.countryEn}</div>
          <h2 className="mt-1 font-display text-3xl font-extrabold text-navy-deep sm:text-4xl">฿{cat.servicePriceTHB.toLocaleString()} — end-to-end</h2>
          <ul className="mt-5 grid gap-2.5 sm:grid-cols-2">
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> ตรวจ eligibility + เตรียมเอกสาร {cat.docsTh.length} รายการ</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> แปลภาษาอังกฤษ + รับรอง MFA</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> ยื่น e-Visa thaievisa.go.th + ติดตามผล</li>
            <li className="flex items-start gap-2 text-sm text-slate-700"><Clock className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> Express 3 วัน (+฿3,500)</li>
          </ul>
          <div className="mt-6"><ContactCTA /></div>
        </section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">คำถามที่พบบ่อย</h2>
          <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-2 sm:p-3">
            <FaqAccordion items={faqs} />
          </div>
        </section>

        <section className="mt-14 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">หมวด DTV อื่นๆ สำหรับชาว{nat.nameTh}</h2>
          <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {otherCats.map((c) => (
              <Link key={c.slug} to="/dtv/$category/for/$nationality" params={{ category: c.slug, nationality: nat.slug }} className="group flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm hover:border-gold/50">
                <span>{c.icon}</span>
                <span className="flex-1 font-semibold text-slate-800 group-hover:text-amber-700">{c.titleTh}</span>
                <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-10 border-t border-slate-200 pt-10">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">{cat.titleTh} สำหรับสัญชาติอื่น</h2>
          <div className="mt-5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {otherNats.map((n) => (
              <Link key={n.slug} to="/dtv/$category/for/$nationality" params={{ category: cat.slug, nationality: n.slug }} className="group flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm hover:border-gold/50">
                <Globe2 className="h-4 w-4 text-amber-600" />
                <span className="flex-1 font-semibold text-slate-800 group-hover:text-amber-700">{n.flag} {n.countryEn}</span>
                <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
              </Link>
            ))}
          </div>
        </section>
      </div>

      <TrustLayer variant="compact" />
    </main>
  );
}
